import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Package, Users, Database,
  MessageCircle, Code, Headphones, IndianRupee, Loader2,
  ToggleLeft, ToggleRight, Shield, X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
  { key: "leads", label: "Leads" },
  { key: "contacts", label: "Contacts" },
  { key: "clients", label: "Clients" },
  { key: "tasks", label: "Tasks" },
  { key: "calendar", label: "Calendar" },
  { key: "automation", label: "Automation" },
  { key: "reports", label: "Reports" },
  { key: "integrations", label: "Integrations" },
  { key: "attendance", label: "Attendance" },
  { key: "payroll", label: "Payroll" },
];

const DEFAULT_MODULES = Object.fromEntries(MODULES.map(m => [m.key, true]));

const emptyPlan = {
  name: "",
  monthly_price: 0,
  yearly_price: 0,
  user_limit: 5,
  lead_limit: 500,
  storage_limit: "1GB",
  module_access: { ...DEFAULT_MODULES },
  whatsapp_integration: false,
  api_access: false,
  support_type: "basic",
};

export default function SubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyPlan });
  const [saving, setSaving] = useState(false);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription-plans`, { credentials: "include" });
      if (res.ok) setPlans(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyPlan, module_access: { ...DEFAULT_MODULES } });
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      name: plan.name || "",
      monthly_price: plan.monthly_price || 0,
      yearly_price: plan.yearly_price || 0,
      user_limit: plan.user_limit || 5,
      lead_limit: plan.lead_limit || 500,
      storage_limit: plan.storage_limit || "1GB",
      module_access: { ...DEFAULT_MODULES, ...(plan.module_access || {}) },
      whatsapp_integration: plan.whatsapp_integration || false,
      api_access: plan.api_access || false,
      support_type: plan.support_type || "basic",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Plan name is required"); return; }
    setSaving(true);
    try {
      const url = editing
        ? `${BACKEND_URL}/api/subscription-plans/${editing.plan_id}`
        : `${BACKEND_URL}/api/subscription-plans`;
      const res = await fetch(url, {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(editing ? "Plan updated" : "Plan created");
        setDialogOpen(false);
        fetchPlans();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save plan");
      }
    } catch (e) {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete "${plan.name}" plan?`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription-plans/${plan.plan_id}`, {
        method: "DELETE", credentials: "include",
      });
      if (res.ok) { toast.success("Plan deleted"); fetchPlans(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed"); }
    } catch (e) { toast.error("Network error"); }
  };

  const handleToggle = async (plan) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription-plans/${plan.plan_id}/toggle`, {
        method: "PATCH", credentials: "include",
      });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchPlans(); }
    } catch (e) { toast.error("Network error"); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="subscription-plans-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Subscription Plans</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Create and manage subscription plans for businesses</p>
        </div>
        <Button onClick={openCreate} data-testid="create-plan-btn" className="bg-amber-500 hover:bg-amber-400 text-white">
          <Plus className="w-4 h-4 mr-2" /> Create Plan
        </Button>
      </div>

      {plans.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
          <Package className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-slate-500 dark:text-slate-400">No plans created yet</p>
          <Button onClick={openCreate} variant="outline" className="mt-4">Create your first plan</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {plans.map(plan => (
            <div key={plan.plan_id} data-testid={`plan-card-${plan.plan_id}`}
              className={`bg-white dark:bg-slate-800 rounded-2xl border p-6 transition-shadow hover:shadow-lg ${
                plan.is_active ? "border-slate-200 dark:border-slate-700" : "border-red-200 dark:border-red-800 opacity-60"
              }`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">{plan.name}</h3>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      plan.is_active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                        : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
                    }`}>{plan.is_active ? "Active" : "Disabled"}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{plan.support_type === "priority" ? "Priority Support" : "Basic Support"}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleToggle(plan)} data-testid={`toggle-plan-${plan.plan_id}`}
                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                    {plan.is_active ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4" />}
                  </button>
                  <button onClick={() => openEdit(plan)} data-testid={`edit-plan-${plan.plan_id}`}
                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-amber-500 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(plan)} data-testid={`delete-plan-${plan.plan_id}`}
                    className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-baseline gap-1 mb-4">
                <IndianRupee className="w-4 h-4 text-slate-400" />
                <span className="text-2xl font-extrabold text-slate-900 dark:text-white">{plan.monthly_price?.toLocaleString("en-IN")}</span>
                <span className="text-xs text-slate-400">/mo</span>
                {plan.yearly_price > 0 && (
                  <span className="text-xs text-slate-400 ml-2">or ₹{plan.yearly_price?.toLocaleString("en-IN")}/yr</span>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <Users className="w-3.5 h-3.5 text-blue-500" /> Up to {plan.user_limit} users
                </div>
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <Database className="w-3.5 h-3.5 text-purple-500" /> {plan.lead_limit?.toLocaleString()} leads &middot; {plan.storage_limit} storage
                </div>
                {plan.whatsapp_integration && (
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <MessageCircle className="w-3.5 h-3.5 text-green-500" /> WhatsApp Integration
                  </div>
                )}
                {plan.api_access && (
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <Code className="w-3.5 h-3.5 text-amber-500" /> API Access
                  </div>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Modules</p>
                <div className="flex flex-wrap gap-1">
                  {MODULES.map(m => (
                    <span key={m.key} className={`text-[10px] px-2 py-0.5 rounded-full ${
                      plan.module_access?.[m.key]
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
                        : "bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500 line-through"
                    }`}>{m.label}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Plan" : "Create Plan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold">Plan Name</Label>
              <Input data-testid="plan-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Growth" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Monthly Price (₹)</Label>
                <Input type="number" data-testid="plan-monthly-price" value={form.monthly_price} onChange={e => setForm({ ...form, monthly_price: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label className="text-xs font-semibold">Yearly Price (₹)</Label>
                <Input type="number" data-testid="plan-yearly-price" value={form.yearly_price} onChange={e => setForm({ ...form, yearly_price: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold">User Limit</Label>
                <Input type="number" value={form.user_limit} onChange={e => setForm({ ...form, user_limit: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label className="text-xs font-semibold">Lead Limit</Label>
                <Input type="number" value={form.lead_limit} onChange={e => setForm({ ...form, lead_limit: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label className="text-xs font-semibold">Storage Limit</Label>
                <Input value={form.storage_limit} onChange={e => setForm({ ...form, storage_limit: e.target.value })} placeholder="e.g. 10GB" />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold mb-2 block">Module Access</Label>
              <div className="grid grid-cols-2 gap-2">
                {MODULES.map(m => (
                  <label key={m.key} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                    <Switch
                      checked={form.module_access[m.key] || false}
                      onCheckedChange={checked => setForm({ ...form, module_access: { ...form.module_access, [m.key]: checked } })}
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={form.whatsapp_integration} onCheckedChange={v => setForm({ ...form, whatsapp_integration: v })} />
                WhatsApp Integration
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={form.api_access} onCheckedChange={v => setForm({ ...form, api_access: v })} />
                API Access
              </label>
            </div>

            <div>
              <Label className="text-xs font-semibold">Support Type</Label>
              <div className="flex gap-3 mt-1">
                {["basic", "priority"].map(t => (
                  <button key={t} onClick={() => setForm({ ...form, support_type: t })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      form.support_type === t ? "border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" : "border-slate-200 dark:border-slate-700 text-slate-500"
                    }`}>
                    <Headphones className="w-3.5 h-3.5 inline mr-1.5" /> {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} data-testid="save-plan-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? "Update Plan" : "Create Plan"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
